import { renderTopbar } from '../components/topbar.js';
import { currentUser, signOutUser, isSchoolOwnerCached, isAdminCached } from '../lib/auth.js';
import { deleteMyAccount } from '../lib/deleteAccount.js';
import { db } from '../firebase.js';
import { doc, getDoc } from 'firebase/firestore';
import { navigate } from '../router.js';
import { confirmDialog, alertDialog } from '../lib/dialog.js';
import { setSchoolCode, requestSchoolJoin } from '../lib/school.js';
import { friendlyError } from '../lib/friendlyError.js';
import { PAYMENTS_ENABLED } from '../lib/featureFlags.js';

export async function renderSettings(container) {
  const user = currentUser();
  container.innerHTML = '';
  container.appendChild(renderTopbar('/settings'));

  const [usageSnap, profileSnap, walletSnap] = await Promise.all([
    getDoc(doc(db, 'users', user.uid, 'meta', 'usage')).catch(() => null),
    getDoc(doc(db, 'users', user.uid, 'profile', 'main')).catch(() => null),
    getDoc(doc(db, 'wallets', user.uid)).catch(() => null),
  ]);
  const usage = usageSnap?.exists() ? usageSnap.data() : { requestCount: 0 };
  const profile = profileSnap?.exists() ? profileSnap.data() : {};
  const wallet = walletSnap?.exists() ? walletSnap.data() : {};

  const theme = localStorage.getItem('ll-theme') || 'light';
  document.documentElement.setAttribute('data-theme', theme);

  // Role-aware panels: a school owner's settings should look like a school
  // account, not a student's — they get a management panel instead of the
  // student "link a code" flow, and admins get a panel for the admin
  // dashboard. Flags come from the server via lib/auth.js.
  const isSchoolOwner = isSchoolOwnerCached();
  const isAdmin = isAdminCached();
  const rolePills = [
    isSchoolOwner ? '<span class="pill">School partner</span>' : '',
    isAdmin ? '<span class="pill">Administrator</span>' : '',
  ].join(' ');

  const schoolOwnerPanel = `
      <div class="settings-panel">
        <h3>🏫 Your school</h3>
        <p style="color:var(--text-dim);font-size:0.88rem;">This account manages a school on Simustage. Buy or renew student seats, enable student content creation, designate teachers, and track your school wallet — all from your dashboard.</p>
        <button class="btn btn-primary" id="open-school-dashboard-btn">Open school dashboard</button>
      </div>`;

  // A student the school added by email (roster) has their membership managed
  // by the school — they don't type a code, and unlinking wouldn't stick, so
  // show a read-only status instead of the code entry.
  const isRosterStudent = !!profile.schoolCode && profile.schoolLinkSource === 'roster';
  // Self-service join (schoolLinkSource:'request', or legacy 'code'): the
  // student's own profile mirrors their request status (schoolJoinStatus)
  // since schools/** — including joinRequests — is unreadable by clients
  // directly (see functions/schools.js requestSchoolJoin). 'approved' isn't
  // a real value here — approval clears schoolJoinStatus and sets
  // profile.schoolCode instead, which is what #school-linked reflects.
  const joinPending = !profile.schoolCode && profile.schoolJoinStatus === 'pending';
  const joinDisallowed = !profile.schoolCode && profile.schoolJoinStatus === 'disallowed';
  const studentSchoolPanel = isRosterStudent
    ? `
      <div class="settings-panel">
        <h3>School / college</h3>
        <p>🏫 Added by <strong>${escapeHtml(profile.schoolName || profile.schoolCode || 'your school')}</strong></p>
        <p style="color:var(--text-dim);font-size:0.88rem;">Your school added you by email, so your access is managed by them — nothing to enter here. If your school removes you, you'll simply continue on your own wallet.</p>
      </div>`
    : `
      <div class="settings-panel">
        <h3>School / college code</h3>
        <div id="school-linked" style="${profile.schoolCode ? '' : 'display:none;'}">
          <p>🏫 Linked to <strong id="school-linked-name">${escapeHtml(profile.schoolName || profile.schoolCode || '')}</strong></p>
          <p style="color:var(--text-dim);font-size:0.88rem;">Your school covers your access — viewing is included, and content creation is available if your school has turned it on.</p>
          <button class="btn btn-sm" id="school-unlink-btn">Unlink</button>
        </div>
        <div id="school-pending" style="${joinPending ? '' : 'display:none;'}">
          <p>⏳ Request sent to <strong id="school-pending-name">${escapeHtml(profile.schoolJoinName || profile.schoolJoinCode || '')}</strong></p>
          <p style="color:var(--text-dim);font-size:0.88rem;">Waiting for your school to approve it — check back later. This is also repeated every year, so you may need to send this request again once your school starts a new year.</p>
        </div>
        <div id="school-unlinked" style="${(profile.schoolCode || joinPending) ? 'display:none;' : ''}">
          <p id="school-disallowed-note" style="color:var(--danger);font-size:0.88rem;${joinDisallowed ? '' : 'display:none;'}">Your last request to join ${escapeHtml(profile.schoolJoinName || profile.schoolJoinCode || 'that school')} wasn't approved. You can try again below.</p>
          <p style="color:var(--text-dim);font-size:0.88rem;">If your school or college gave you a code, enter it here to request access — your school reviews and approves each request, and covers your access once approved.</p>
          <div class="field"><input type="text" id="school-code-input" placeholder="e.g. RishikulGohana" autocomplete="off" /></div>
          <button class="btn btn-primary" id="school-link-btn">Request to join</button>
        </div>
        <div id="school-msg" style="font-size:0.85rem;min-height:1.2em;margin-top:6px;"></div>
      </div>`;

  // Subscription panel — reflects the REAL billing model (see
  // functions/wallet.js ensureViewingAccess + the public pricing page):
  // a 30-day free period for every new account, then ₹100/month viewing
  // auto-debited from the wallet; creating and buying are pay-as-you-go.
  // School-linked accounts show "covered by your school" instead, and a
  // paused (insufficient balance) state points at the wallet. The wallet
  // doc is owner-readable (firestore.rules); reading it here has no side
  // effects — actual renewals only ever happen at viewing time.
  const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString() : null);
  const periodEndMs = wallet.viewingPeriodEnd?.toMillis?.() ?? null;
  let subscriptionPanel;
  if (profile.schoolCode) {
    subscriptionPanel = `
      <div class="settings-panel">
        <h3>Subscription</h3>
        <p><span class="pill">Covered by your school</span></p>
        <p style="color:var(--text-dim);font-size:0.88rem;">
          Your viewing access is sponsored by <strong>${escapeHtml(profile.schoolName || 'your school')}</strong> while
          its licence is active — no monthly charge from your own wallet. Content
          creation, if your school has enabled it, is billed to the school as well.
        </p>
      </div>`;
  } else {
    const status = wallet.viewingStatus || null;
    const statusPill = status === 'blocked'
      ? '<span class="pill" style="background:var(--danger);color:#fff;">Viewing paused</span>'
      : status === 'trialing'
        ? '<span class="pill">Free period</span>'
        : status === 'active'
          ? '<span class="pill">Active</span>'
          : '<span class="pill">Free month on first use</span>';
    const statusLine = !PAYMENTS_ENABLED
      ? 'Payments aren’t live yet during this pilot — viewing is free for now.'
      : status === 'blocked'
        ? 'Your last renewal couldn’t be charged — recharge your wallet and viewing resumes automatically the next time you open your content. Nothing has been deleted.'
        : periodEndMs
          ? `Your viewing access is paid up until <strong>${fmtDate(periodEndMs)}</strong>, then renews at ₹100/month from your wallet.`
          : 'Every account gets its first 30 days of viewing free — after that it’s ₹100/month, auto-charged from your wallet.';
    subscriptionPanel = `
      <div class="settings-panel">
        <h3>Subscription</h3>
        <p>${statusPill}</p>
        <p style="color:var(--text-dim);font-size:0.88rem;">${statusLine}</p>
        ${PAYMENTS_ENABLED ? `
        <p style="color:var(--text-dim);font-size:0.88rem;">
          Creating content and buying Library listings are separate pay-as-you-go
          charges from your wallet balance — you only pay for what you use.
        </p>
        <button class="btn" id="open-wallet-btn">Open wallet</button>` : ''}
      </div>`;
  }

  const adminPanel = isAdmin ? `
      <div class="settings-panel">
        <h3>🛠️ Administration</h3>
        <p style="color:var(--text-dim);font-size:0.88rem;">This account is a site administrator. Manage model routing, commerce settings, schools, recommenders, and complaints from the admin dashboard.</p>
        <button class="btn btn-primary" id="open-admin-dashboard-btn">Open admin dashboard</button>
      </div>` : '';

  const main = document.createElement('div');
  main.className = 'main-full';
  main.innerHTML = `
    <h1>Settings</h1>
    <div class="settings-grid">
      <div class="settings-panel">
        <h3>Profile</h3>
        <p><strong>Name:</strong> ${escapeHtml(user.displayName || '—')} ${rolePills}</p>
        <p><strong>Email:</strong> ${escapeHtml(user.email || '—')}</p>
        <p><strong>Generations so far:</strong> ${usage.requestCount || 0}</p>
      </div>
      ${isSchoolOwner ? schoolOwnerPanel : ''}
      ${adminPanel}

      <div class="settings-panel">
        <h3>Appearance</h3>
        <label style="display:flex;align-items:center;gap:8px;">
          <input type="checkbox" id="dark-toggle" ${theme === 'dark' ? 'checked' : ''} />
          Dark mode
        </label>
      </div>

      ${isSchoolOwner ? '' : studentSchoolPanel}

      ${subscriptionPanel}

      <div class="settings-panel">
        <h3 style="color:var(--danger);">Danger zone</h3>
        <p style="color:var(--text-dim);font-size:0.88rem;">
          Deleting your account permanently removes every course, topic, version,
          chat history, your Library listings, and your wallet — any remaining
          wallet balance is forfeited. This cannot be undone.
        </p>
        <button class="btn btn-danger" id="delete-account-btn">Delete account</button>
      </div>
    </div>
  `;
  container.appendChild(main);

  // Role-panel shortcuts (only rendered for the matching role).
  main.querySelector('#open-school-dashboard-btn')?.addEventListener('click', () => navigate('/school'));
  main.querySelector('#open-admin-dashboard-btn')?.addEventListener('click', () => navigate('/dwar'));
  main.querySelector('#open-wallet-btn')?.addEventListener('click', () => navigate('/wallet'));

  // School code link/unlink — student/teacher accounts only (school owners
  // get the management panel above instead). Validation happens server-side
  // (setSchoolCode checks the code exists, is enabled, and has a seat).
  const schoolMsg = main.querySelector('#school-msg');
  const linkedPanel = main.querySelector('#school-linked');
  const pendingPanel = main.querySelector('#school-pending');
  const unlinkedPanel = main.querySelector('#school-unlinked');

  main.querySelector('#school-link-btn')?.addEventListener('click', async () => {
    const btn = main.querySelector('#school-link-btn');
    const code = main.querySelector('#school-code-input').value.trim();
    schoolMsg.style.color = 'var(--danger)';
    schoolMsg.textContent = '';
    if (!code) { schoolMsg.textContent = 'Enter your school code.'; return; }
    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      const res = await requestSchoolJoin(code);
      if (res.status === 'approved') {
        main.querySelector('#school-linked-name').textContent = res.schoolName || code;
        linkedPanel.style.display = '';
        pendingPanel.style.display = 'none';
        unlinkedPanel.style.display = 'none';
        schoolMsg.style.color = 'var(--success)';
        schoolMsg.textContent = 'Linked! Your school now covers your access.';
      } else {
        main.querySelector('#school-pending-name').textContent = res.schoolName || code;
        linkedPanel.style.display = 'none';
        pendingPanel.style.display = '';
        unlinkedPanel.style.display = 'none';
        schoolMsg.style.color = 'var(--success)';
        schoolMsg.textContent = 'Request sent — your school will review it.';
      }
    } catch (err) {
      schoolMsg.textContent = friendlyError(err, 'Could not send that request.');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Request to join';
    }
  });

  main.querySelector('#school-unlink-btn')?.addEventListener('click', async () => {
    const btn = main.querySelector('#school-unlink-btn');
    btn.disabled = true;
    try {
      await setSchoolCode(null);
      linkedPanel.style.display = 'none';
      pendingPanel.style.display = 'none';
      unlinkedPanel.style.display = '';
      schoolMsg.style.color = 'var(--text-dim)';
      schoolMsg.textContent = 'School unlinked.';
    } catch (err) {
      schoolMsg.style.color = 'var(--danger)';
      schoolMsg.textContent = friendlyError(err, 'Could not unlink.');
    } finally {
      btn.disabled = false;
    }
  });

  main.querySelector('#dark-toggle').addEventListener('change', (e) => {
    const t = e.target.checked ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', t);
    localStorage.setItem('ll-theme', t);
  });

  main.querySelector('#delete-account-btn').addEventListener('click', async () => {
    const first = await confirmDialog('This permanently deletes your account, all content, your listings, and your wallet (any remaining balance is forfeited). Continue?', {
      title: 'Delete account?', okText: 'Continue', danger: true,
    });
    if (!first) return;
    const second = await confirmDialog('Are you absolutely sure? This cannot be undone.', {
      title: 'Last chance', okText: 'Delete my account', danger: true,
    });
    if (!second) return;
    try {
      // Server-side deletion (functions/accountDeletion.js): guards run
      // first, so ANY failure means nothing was removed — stay signed in
      // and surface the reason (school ownership, buyers with active
      // access, transient network, ...). Retrying is always safe.
      await deleteMyAccount();
    } catch (e) {
      await alertDialog(
        friendlyError(e, 'Could not delete your account. Please retry in a moment.'),
        { title: "Couldn't delete account" }
      );
      return;
    }
    // Success: data AND the Firebase Auth account are gone. End the local
    // session explicitly so this device lands signed-out on the public
    // landing page.
    await signOutUser().catch(() => {});
    await alertDialog('Your account and all your data have been deleted.', { title: 'Account deleted' });
    navigate('/');
  });

  return () => {};
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
